import { useState, useEffect, createContext, useContext } from 'react';

const API = `${window.location.origin}/api`;

// Feature flags context
const FeatureFlagsContext = createContext({
  marketplaceEnabled: false,
  deliveryEnabled: false,
  melaninAccountEnabled: false,
  loading: true,
  refresh: () => {}
});

export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState({
    marketplaceEnabled: false,
    deliveryEnabled: false,
    melaninAccountEnabled: false,
    loading: true
  });

  const fetchFlags = async () => {
    try {
      // Fetch public settings (doesn't require auth)
      const res = await fetch(`${API}/settings/public`);
      
      if (res.ok) {
        const data = await res.json();
        setFlags({
          marketplaceEnabled: data.marketplace_enabled || false,
          deliveryEnabled: data.delivery_service_enabled || false,
          melaninAccountEnabled: data.melanin_account_enabled || false,
          loading: false
        });
      } else {
        setFlags(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      setFlags(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ ...flags, refresh: fetchFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

// Hook to check if a specific business has marketplace enabled
export function useBusinessFeatures(businessId) {
  const [features, setFeatures] = useState({
    marketplaceEnabled: false,
    loading: true
  });
  const globalFlags = useFeatureFlags();

  useEffect(() => {
    if (!businessId || !globalFlags.marketplaceEnabled) {
      setFeatures({ marketplaceEnabled: false, loading: false });
      return;
    }

    const fetchBusinessFeatures = async () => {
      try {
        const res = await fetch(`${API}/businesses/${businessId}`, {
          credentials: 'include'
        });
        
        if (res.ok) {
          const data = await res.json();
          setFeatures({
            marketplaceEnabled: data.marketplace_enabled !== false,
            loading: false
          });
        } else {
          setFeatures({ marketplaceEnabled: false, loading: false });
        }
      } catch (error) {
        setFeatures({ marketplaceEnabled: false, loading: false });
      }
    };

    fetchBusinessFeatures();
  }, [businessId, globalFlags.marketplaceEnabled]);

  return features;
}

// Hook to check if current user has delivery enabled
export function useUserDeliveryAccess() {
  const [access, setAccess] = useState({
    enabled: false,
    isDriver: false,
    loading: true
  });
  const globalFlags = useFeatureFlags();

  useEffect(() => {
    if (!globalFlags.deliveryEnabled) {
      setAccess({ enabled: false, isDriver: false, loading: false });
      return;
    }

    const checkAccess = async () => {
      try {
        const res = await fetch(`${API}/delivery/status`, {
          credentials: 'include'
        });
        
        if (res.ok) {
          const data = await res.json();
          setAccess({
            enabled: true,
            isDriver: data.is_driver || false,
            canRegister: data.can_register || false,
            loading: false
          });
        } else {
          setAccess({ enabled: true, isDriver: false, loading: false });
        }
      } catch (error) {
        setAccess({ enabled: false, isDriver: false, loading: false });
      }
    };

    checkAccess();
  }, [globalFlags.deliveryEnabled]);

  return access;
}

export default FeatureFlagsContext;
